import * as THREE from 'three';

/**
 * Contact pins — a CapCut-simple take on MotionBuilder's contact points /
 * reach: pin a hand or foot to a world point or another actor's bone, with a
 * keyframable influence (0 = raw mocap, 1 = locked to the target) blended by a
 * two-bone analytic IK on the puppet's limb chain.
 */

export type ContactLimb = 'lefthand' | 'righthand' | 'leftfoot' | 'rightfoot';

export type ContactTarget =
  | { kind: 'point'; point: [number, number, number] }
  | {
      kind: 'bone';
      actorId: string;
      bone: string;
      /** Offset from the bone in ITS local frame (metres) — "5cm up the wrist". */
      offset?: [number, number, number];
      /** Also orient the pinned end bone to the target bone's frame. */
      matchRotation?: boolean;
      /** Euler offset (degrees XYZ) applied on top of the matched rotation. */
      rotate?: [number, number, number];
    };

/** Influence keyframe (time in seconds, value 0..1). */
export interface InfluenceKey {
  time: number;
  value: number;
}

export interface ContactPin {
  id: string;
  /** Whose limb is pinned. */
  actorId: string;
  limb: ContactLimb;
  target: ContactTarget;
  /** Sorted influence keys; smoothstep-interpolated between neighbours. */
  keys: InfluenceKey[];
  /** Track on/off switch (timeline chip) — disabled pins are skipped entirely. */
  enabled: boolean;
}

/** The limb chain (upper / lower / end) canonicals for each pinnable limb. */
export const LIMB_CHAINS: Record<ContactLimb, [string, string, string]> = {
  lefthand: ['leftarm', 'leftforearm', 'lefthand'],
  righthand: ['rightarm', 'rightforearm', 'righthand'],
  leftfoot: ['leftupleg', 'leftleg', 'leftfoot'],
  rightfoot: ['rightupleg', 'rightleg', 'rightfoot'],
};

export const LIMB_LABELS: Record<ContactLimb, string> = {
  lefthand: 'Left hand',
  righthand: 'Right hand',
  leftfoot: 'Left foot',
  rightfoot: 'Right foot',
};

/** Validate contact pins parsed from a scene file (drops malformed entries). */
export function sanitizeContacts(raw: unknown): ContactPin[] {
  if (!Array.isArray(raw)) return [];
  const pins: ContactPin[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const p = item as Partial<ContactPin>;
    if (typeof p.id !== 'string' || typeof p.actorId !== 'string') continue;
    if (!p.limb || !(p.limb in LIMB_CHAINS)) continue;
    const t = p.target as ContactTarget | undefined;
    let target: ContactTarget;
    if (t?.kind === 'point' && Array.isArray(t.point) && t.point.length === 3) {
      target = { kind: 'point', point: [t.point[0], t.point[1], t.point[2]] };
    } else if (t?.kind === 'bone' && typeof t.actorId === 'string' && typeof t.bone === 'string') {
      const vec3 = (v: unknown): [number, number, number] | undefined =>
        Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === 'number')
          ? [v[0], v[1], v[2]]
          : undefined;
      target = {
        kind: 'bone',
        actorId: t.actorId,
        bone: t.bone,
        offset: vec3(t.offset),
        matchRotation: t.matchRotation === true,
        rotate: vec3(t.rotate),
      };
    } else {
      continue;
    }
    const keys = Array.isArray(p.keys)
      ? p.keys
          .filter((k) => typeof k?.time === 'number' && typeof k?.value === 'number')
          .map((k) => ({ time: Math.max(0, k.time), value: Math.min(Math.max(k.value, 0), 1) }))
          .sort((a, b) => a.time - b.time)
      : [];
    pins.push({
      id: p.id,
      actorId: p.actorId,
      limb: p.limb,
      target,
      keys,
      enabled: p.enabled !== false,
    });
  }
  return pins;
}

/**
 * Influence at time t: smoothstep between neighbouring keys (no jarring snaps),
 * clamped to the first/last key outside the keyed range. No keys → 0.
 */
export function evalInfluence(keys: InfluenceKey[], t: number): number {
  if (keys.length === 0) return 0;
  if (t <= keys[0].time) return keys[0].value;
  const last = keys[keys.length - 1];
  if (t >= last.time) return last.value;
  for (let i = 0; i < keys.length - 1; i++) {
    const k1 = keys[i];
    const k2 = keys[i + 1];
    if (t >= k1.time && t <= k2.time) {
      const span = k2.time - k1.time;
      const s = span > 1e-9 ? (t - k1.time) / span : 1;
      const eased = s * s * (3 - 2 * s);
      return k1.value + (k2.value - k1.value) * eased;
    }
  }
  return last.value;
}

// --- Two-bone analytic IK ------------------------------------------------------------

const a = new THREE.Vector3();
const b = new THREE.Vector3();
const c = new THREE.Vector3();
const acV = new THREE.Vector3();
const abV = new THREE.Vector3();
const baV = new THREE.Vector3();
const bcV = new THREE.Vector3();
const atV = new THREE.Vector3();
const axis0 = new THREE.Vector3();
const axis1 = new THREE.Vector3();
const tmpQ = new THREE.Quaternion();
const rotQ = new THREE.Quaternion();
const worldQ = new THREE.Quaternion();
const parentQ = new THREE.Quaternion();
const upperLocal0 = new THREE.Quaternion();
const lowerLocal0 = new THREE.Quaternion();

function clamp01(v: number): number {
  return Math.min(Math.max(v, -1), 1);
}

/** Premultiply a world-space rotation onto a bone (keeps children attached). */
function rotateWorld(bone: THREE.Object3D, q: THREE.Quaternion): void {
  bone.getWorldQuaternion(worldQ);
  if (bone.parent) {
    bone.parent.getWorldQuaternion(parentQ);
    bone.quaternion.copy(parentQ.invert()).multiply(tmpQ.copy(q).multiply(worldQ));
  } else {
    bone.quaternion.copy(tmpQ.copy(q).multiply(worldQ));
  }
  bone.updateMatrixWorld(true);
}

/**
 * Classic two-bone IK (Ryan Juckett formulation): bend the elbow/knee to make
 * the end reachable, then aim the chain at the target — finally blended toward
 * the original pose by `weight`. The bend stays in the limb's current plane
 * (the mocap pose supplies the pole), which avoids elbow flips.
 */
export function solveTwoBoneIK(
  upper: THREE.Object3D,
  lower: THREE.Object3D,
  end: THREE.Object3D,
  target: THREE.Vector3,
  weight: number
): void {
  if (weight <= 0) return;

  upperLocal0.copy(upper.quaternion);
  lowerLocal0.copy(lower.quaternion);

  upper.updateWorldMatrix(true, false);
  upper.getWorldPosition(a);
  lower.updateWorldMatrix(false, false);
  lower.getWorldPosition(b);
  end.updateWorldMatrix(false, false);
  end.getWorldPosition(c);

  const lab = a.distanceTo(b);
  const lcb = b.distanceTo(c);
  if (lab < 1e-6 || lcb < 1e-6) return;
  atV.copy(target).sub(a);
  const lat = Math.min(Math.max(atV.length(), 0.01), (lab + lcb) * 0.999);

  acV.copy(c).sub(a);
  abV.copy(b).sub(a);
  baV.copy(a).sub(b);
  bcV.copy(c).sub(b);
  const lac = Math.max(acV.length(), 1e-6);

  // Current and required interior angles (law of cosines).
  const acAb0 = Math.acos(clamp01(acV.dot(abV) / (lac * lab)));
  const baBc0 = Math.acos(clamp01(baV.dot(bcV) / (lab * lcb)));
  const acAb1 = Math.acos(clamp01((lcb * lcb - lab * lab - lat * lat) / (-2 * lab * lat)));
  const baBc1 = Math.acos(clamp01((lat * lat - lab * lab - lcb * lcb) / (-2 * lab * lcb)));

  // Bend plane normal from the current pose; degenerate (straight) limbs fall
  // back to any axis perpendicular to the target direction.
  axis0.crossVectors(acV, abV);
  if (axis0.lengthSq() < 1e-8) {
    axis0.crossVectors(atV, new THREE.Vector3(0, 1, 0));
    if (axis0.lengthSq() < 1e-8) axis0.set(1, 0, 0);
  }
  axis0.normalize();

  rotateWorld(upper, rotQ.setFromAxisAngle(axis0, acAb1 - acAb0));
  rotateWorld(lower, rotQ.setFromAxisAngle(axis0, baBc1 - baBc0));

  // Aim the (now correctly bent) chain at the target: rotating around
  // cross(ac, at) by the angle between them carries the end onto the target ray.
  end.updateWorldMatrix(true, false);
  end.getWorldPosition(c);
  acV.copy(c).sub(a);
  axis1.crossVectors(acV, atV);
  if (axis1.lengthSq() > 1e-8) {
    axis1.normalize();
    const lac2 = Math.max(acV.length(), 1e-6);
    const aim = Math.acos(clamp01(acV.dot(atV) / (lac2 * lat)));
    rotateWorld(upper, rotQ.setFromAxisAngle(axis1, aim));
  }

  // Blend solved ↔ original locals by influence (parents are unchanged).
  if (weight < 1) {
    upper.quaternion.copy(upperLocal0.slerp(upper.quaternion, weight));
    lower.quaternion.copy(lowerLocal0.slerp(lower.quaternion, weight));
  }
  upper.updateMatrixWorld(true);
}

// --- Stateful solver (idempotence over raw source bones) -------------------------------

interface PinMemo {
  upper: THREE.Object3D;
  lower: THREE.Object3D;
  end: THREE.Object3D;
  upperRaw: THREE.Quaternion;
  lowerRaw: THREE.Quaternion;
  endRaw: THREE.Quaternion;
  upperWritten: THREE.Quaternion;
  lowerWritten: THREE.Quaternion;
  endWritten: THREE.Quaternion;
  has: boolean;
}

/** Optional reach-rotation: orient the pinned end bone to the target's frame. */
export interface OrientSpec {
  targetBone: THREE.Object3D;
  /** Euler degrees applied on top of the target bone's world rotation. */
  rotate: [number, number, number];
}

export interface ContactSolver {
  solve: (
    pinId: string,
    upper: THREE.Object3D,
    lower: THREE.Object3D,
    end: THREE.Object3D,
    target: THREE.Vector3,
    weight: number,
    orient?: OrientSpec | null
  ) => void;
}

function quatClose(a: THREE.Quaternion, b: THREE.Quaternion): boolean {
  return 2 * Math.acos(Math.min(Math.abs(a.dot(b)), 1)) < 1e-4;
}

/**
 * Wraps solveTwoBoneIK with per-pin raw/written tracking, the same guard the
 * finger healer uses: puppet bones are rewritten by the retargeter every tick,
 * but SOURCE bones are not re-posed by the mixer while playback is paused — a
 * bare-skeleton pin would otherwise re-solve on top of its own output every
 * rAF and compound. If a chain bone still holds exactly what we last wrote,
 * the raw pose is restored before solving again.
 */
const orientTargetQ = new THREE.Quaternion();
const orientOffsetQ = new THREE.Quaternion();
const orientEuler = new THREE.Euler();
const endParentQ = new THREE.Quaternion();
const endWorldQ = new THREE.Quaternion();

export function createContactSolver(): ContactSolver {
  const memos = new Map<string, PinMemo>();

  function solve(
    pinId: string,
    upper: THREE.Object3D,
    lower: THREE.Object3D,
    end: THREE.Object3D,
    target: THREE.Vector3,
    weight: number,
    orient?: OrientSpec | null
  ): void {
    let memo = memos.get(pinId);
    if (!memo || memo.upper !== upper || memo.lower !== lower || memo.end !== end) {
      memo = {
        upper,
        lower,
        end,
        upperRaw: new THREE.Quaternion(),
        lowerRaw: new THREE.Quaternion(),
        endRaw: new THREE.Quaternion(),
        upperWritten: new THREE.Quaternion(),
        lowerWritten: new THREE.Quaternion(),
        endWritten: new THREE.Quaternion(),
        has: false,
      };
      memos.set(pinId, memo);
    }

    if (
      memo.has &&
      quatClose(upper.quaternion, memo.upperWritten) &&
      quatClose(lower.quaternion, memo.lowerWritten) &&
      quatClose(end.quaternion, memo.endWritten)
    ) {
      // Nothing re-posed these bones since our last write — recover the raw pose.
      upper.quaternion.copy(memo.upperRaw);
      lower.quaternion.copy(memo.lowerRaw);
      end.quaternion.copy(memo.endRaw);
      upper.updateMatrixWorld(true);
    } else {
      memo.upperRaw.copy(upper.quaternion);
      memo.lowerRaw.copy(lower.quaternion);
      memo.endRaw.copy(end.quaternion);
      memo.has = true;
    }

    solveTwoBoneIK(upper, lower, end, target, weight);

    // Reach-rotation: orient the end bone to the target bone's frame (+euler),
    // slerped by the same influence.
    if (orient) {
      orient.targetBone.getWorldQuaternion(orientTargetQ);
      orientEuler.set(
        orient.rotate[0] * (Math.PI / 180),
        orient.rotate[1] * (Math.PI / 180),
        orient.rotate[2] * (Math.PI / 180)
      );
      orientTargetQ.multiply(orientOffsetQ.setFromEuler(orientEuler));
      end.getWorldQuaternion(endWorldQ);
      endWorldQ.slerp(orientTargetQ, weight);
      if (end.parent) {
        end.parent.getWorldQuaternion(endParentQ);
        end.quaternion.copy(endParentQ.invert()).multiply(endWorldQ);
      } else {
        end.quaternion.copy(endWorldQ);
      }
      end.updateMatrixWorld(true);
    }

    memo.upperWritten.copy(upper.quaternion);
    memo.lowerWritten.copy(lower.quaternion);
    memo.endWritten.copy(end.quaternion);
  }

  return { solve };
}
